// frontend/src/pages/doctor/ManageSubscription.js
import React, { useState, useEffect } from 'react';
import { CreditCard, Check, X, AlertCircle, Crown, Zap, Clock } from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ManageSubscription = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    mpesaPhone: '',
    mpesaCode: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      setLoading(true);
      const response = await apiService.doctors.getSubscription();
      
      if (response.success) {
        setSubscriptionInfo(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (planKey) => {
    // Check if there's a pending payment
    if (subscriptionInfo?.pendingPayment) {
      setError('You already have a pending payment. Please wait for admin approval or contact support.');
      return;
    }

    const plan = subscriptionInfo.plans[planKey];
    setSelectedPlan({ key: planKey, ...plan });
    
    if (planKey === 'free') {
      // Can switch to free immediately
      handleSubscribe(planKey);
    } else {
      // Show payment modal for paid plans
      setShowPaymentModal(true);
      setError('');
    }
  };

  const handleSubscribe = async (planKey = selectedPlan?.key) => {
    if (!planKey) return;

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const subscriptionData = {
        plan: planKey,
        paymentMethod: 'mpesa'
      };

      // For paid plans, include payment details
      if (planKey !== 'free') {
        if (!paymentData.mpesaPhone || !paymentData.mpesaCode) {
          setError('Please provide M-Pesa phone number and transaction code');
          return;
        }

        subscriptionData.mpesaPhone = paymentData.mpesaPhone;
        subscriptionData.mpesaCode = paymentData.mpesaCode;
      }

      const response = await apiService.doctors.subscribe(subscriptionData);
      
      if (response.success) {
        if (planKey === 'free') {
          setSuccess('Subscription changed to Free plan');
        } else {
          setSuccess('Payment submitted successfully! Your subscription will be activated once admin approves the payment.');
        }
        setShowPaymentModal(false);
        setPaymentData({ mpesaPhone: '', mpesaCode: '' });
        
        // Refresh subscription info
        setTimeout(() => {
          fetchSubscriptionInfo();
          setSuccess('');
        }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to process subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanIcon = (planKey) => {
    const icons = {
      free: <Zap className="w-8 h-8" />,
      basic: <Check className="w-8 h-8" />,
      professional: <CreditCard className="w-8 h-8" />,
      premium: <Crown className="w-8 h-8" />
    };
    return icons[planKey] || <Check className="w-8 h-8" />;
  };

  const getPlanColor = (planKey) => {
    const colors = {
      free: 'from-gray-500 to-gray-700',
      basic: 'from-blue-500 to-blue-700',
      professional: 'from-purple-500 to-purple-700',
      premium: 'from-yellow-500 to-yellow-700'
    };
    return colors[planKey] || 'from-gray-500 to-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const currentPlan = subscriptionInfo?.subscription?.plan || 'free';
  const usage = subscriptionInfo?.usage || {};
  const pendingPayment = subscriptionInfo?.pendingPayment;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Manage Subscription</h1>
        <p className="text-gray-600 mt-1">Choose the plan that works best for your practice</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Pending Payment Alert */}
      {pendingPayment && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
          <div className="flex items-start">
            <Clock className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-800">Payment Pending Approval</h4>
              <p className="text-yellow-700 text-sm mt-1">
                Your <strong>{pendingPayment.plan.toUpperCase()}</strong> plan payment 
                (KES {pendingPayment.amount.toLocaleString()}) is awaiting admin approval.
              </p>
              <p className="text-yellow-600 text-xs mt-2">
                Submitted on {new Date(pendingPayment.submittedAt).toLocaleString()}
              </p>
              <p className="text-yellow-600 text-xs mt-1">
                Please wait for approval before submitting another payment. Contact support if you have questions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Usage */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Current Usage</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">Current Plan</p>
            <p className="text-2xl font-bold text-primary-600 mt-1 capitalize">
              {currentPlan}
            </p>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">Consultations Used</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {usage.used || 0} / {usage.limit === 0 ? '∞' : usage.limit}
            </p>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">Remaining</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {usage.remaining === 'unlimited' ? '∞' : usage.remaining}
            </p>
          </div>
        </div>

        {!usage.canAccept && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-800">Consultation Limit Reached</h4>
                <p className="text-red-700 text-sm mt-1">
                  You've reached your monthly consultation limit. Upgrade your plan to accept more consultations.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {subscriptionInfo?.plans && Object.entries(subscriptionInfo.plans).map(([planKey, plan]) => {
          const isCurrentPlan = planKey === currentPlan;
          const isPendingPlan = pendingPayment && pendingPayment.plan === planKey;
          const features = plan.features;

          return (
            <div
              key={planKey}
              className={`bg-white rounded-lg shadow-md overflow-hidden ${
                isCurrentPlan ? 'ring-2 ring-primary-600' : ''
              } ${isPendingPlan ? 'ring-2 ring-yellow-500' : ''}`}
            >
              {/* Plan Header */}
              <div className={`bg-gradient-to-br ${getPlanColor(planKey)} text-white p-6`}>
                <div className="flex justify-center mb-3">
                  {getPlanIcon(planKey)}
                </div>
                <h3 className="text-xl font-bold text-center">{plan.name}</h3>
                
                <div className="mt-4 text-center">
                  <span className="text-3xl font-bold">KES {plan.price.toLocaleString()}</span>
                  <span className="text-sm opacity-90">/month</span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    {features.unlimitedConsultations ? (
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Check className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm text-gray-700">
                      {features.unlimitedConsultations 
                        ? 'Unlimited consultations' 
                        : `${features.maxConsultations} consultations/month`}
                    </span>
                  </li>
                  
                  <li className="flex items-start">
                    {features.onlineBooking ? (
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${features.onlineBooking ? 'text-gray-700' : 'text-gray-400'}`}>
                      Online booking
                    </span>
                  </li>
                  
                  <li className="flex items-start">
                    {features.analytics ? (
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${features.analytics ? 'text-gray-700' : 'text-gray-400'}`}>
                      Analytics dashboard
                    </span>
                  </li>
                  
                  <li className="flex items-start">
                    {features.prescriptionTemplates ? (
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${features.prescriptionTemplates ? 'text-gray-700' : 'text-gray-400'}`}>
                      Prescription templates
                    </span>
                  </li>
                  
                  <li className="flex items-start">
                    {features.prioritySupport ? (
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${features.prioritySupport ? 'text-gray-700' : 'text-gray-400'}`}>
                      Priority support
                    </span>
                  </li>
                </ul>

                {/* Action Button */}
                {isPendingPlan ? (
                  <button
                    disabled
                    className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-medium cursor-not-allowed flex items-center justify-center"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Pending Approval
                  </button>
                ) : isCurrentPlan ? (
                  <button
                    disabled
                    className="w-full px-4 py-2 bg-gray-200 text-gray-600 rounded-lg font-medium cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handlePlanSelect(planKey)}
                    disabled={submitting || !!pendingPayment}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {planKey === 'free' ? 'Downgrade' : 'Upgrade'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Subscribe to {selectedPlan.name}
            </h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Amount:</strong> KES {selectedPlan.price.toLocaleString()}/month
              </p>
              <p className="text-sm text-blue-800 mt-2">
                Please complete payment via M-Pesa, then enter your details below.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  M-Pesa Phone Number *
                </label>
                <input
                  type="tel"
                  value={paymentData.mpesaPhone}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, mpesaPhone: e.target.value }))}
                  placeholder="+254712345678"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  M-Pesa Transaction Code *
                </label>
                <input
                  type="text"
                  value={paymentData.mpesaCode}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, mpesaCode: e.target.value.toUpperCase() }))}
                  placeholder="e.g., QABCDEFG123"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  Your payment will be verified by admin. Subscription will be activated once approved (usually within 24 hours).
                </p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                  setPaymentData({ mpesaPhone: '', mpesaCode: '' });
                  setError('');
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSubscribe()}
                disabled={submitting || !paymentData.mpesaPhone || !paymentData.mpesaCode}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Instructions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">How to Subscribe</h3>
        
        <ol className="space-y-3 text-sm text-gray-700">
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 text-xs font-bold">
              1
            </span>
            <span>Choose your desired subscription plan above</span>
          </li>
          
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 text-xs font-bold">
              2
            </span>
            <span>Complete M-Pesa payment using the paybill or till number provided</span>
          </li>
          
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 text-xs font-bold">
              3
            </span>
            <span>Enter your M-Pesa phone number and transaction code</span>
          </li>
          
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 text-xs font-bold">
              4
            </span>
            <span>Wait for admin approval (usually within 24 hours)</span>
          </li>
          
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 text-xs font-bold">
              5
            </span>
            <span>Your subscription will be activated and you can start accepting more consultations!</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default ManageSubscription;